import { getCurrentPeriod, getNextNPeriodNumbers, getShortMonth } from "../../../../common/util/period";
import {
  GROUP_SOP_MOCK_DATA,
  SOP_PRODUCT_IMAGES,
  SOP_PRODUCT_NAMES,
} from "../../../ActionReports/GroupSopReport/_mockdata";

export type SopSubscription = {
  productName: string;
  startDate: string;
  endDate: string;
  productPV: string;
  frequency: number;
  quantity: number;
  promoMonths?: string[];
  imgUrl?: string;
};

export type SopStatusKpis = {
  productsOnSop: number;
  productsShippingThisMonth: number;
  ordersUpForRenewal: number;
  expectedPVThisMonth: string;
  expectedPVNextMonth: string;
};

export const SOP_STATUS_KPI_ORDER: (keyof SopStatusKpis)[] = [
  "productsOnSop",
  "productsShippingThisMonth",
  "ordersUpForRenewal",
  "expectedPVThisMonth",
  "expectedPVNextMonth",
];

export const SOP_STATUS_KPIS: SopStatusKpis = {
  productsOnSop: 3,
  productsShippingThisMonth: 3,
  ordersUpForRenewal: 1,
  expectedPVThisMonth: "2,200",
  expectedPVNextMonth: "1,800",
};

export const SOP_STATUS_SUBSCRIPTIONS: SopSubscription[] = [
  {
    productName: SOP_PRODUCT_NAMES[0],
    startDate: "202511",
    endDate: "202604",
    quantity: 1,
    frequency: 3,
    productPV: "780",
    promoMonths: ["202601"],
    imgUrl: SOP_PRODUCT_IMAGES[0],
  },
  {
    productName: SOP_PRODUCT_NAMES[1],
    startDate: "202610",
    endDate: "202709",
    quantity: 1,
    frequency: 1,
    productPV: "1,800",
    promoMonths: ["202709"],
    imgUrl: SOP_PRODUCT_IMAGES[1],
  },
  {
    productName: SOP_PRODUCT_NAMES[2],
    startDate: "202608",
    endDate: "202610",
    quantity: 2,
    frequency: 1,
    productPV: "400",
    imgUrl: SOP_PRODUCT_IMAGES[2],
  },
];

const SOP_PRODUCT_DETAILS: Record<string, Omit<SopSubscription, "imgUrl">> = {
  [SOP_PRODUCT_IMAGES[0]]: {
    productName: SOP_PRODUCT_NAMES[0],
    startDate: "202511",
    endDate: "202604",
    quantity: 1,
    frequency: 3,
    productPV: "780",
    promoMonths: ["202601"],
  },
  [SOP_PRODUCT_IMAGES[1]]: {
    productName: SOP_PRODUCT_NAMES[1],
    startDate: "202610",
    endDate: "202709",
    quantity: 1,
    frequency: 1,
    productPV: "1,800",
    promoMonths: ["202709"],
  },
  [SOP_PRODUCT_IMAGES[2]]: {
    productName: SOP_PRODUCT_NAMES[2],
    startDate: "202608",
    endDate: "202610",
    quantity: 2,
    frequency: 1,
    productPV: "400",
  },
  [SOP_PRODUCT_IMAGES[3]]: {
    productName: SOP_PRODUCT_NAMES[3],
    startDate: "202604",
    endDate: "202703",
    quantity: 1,
    frequency: 2,
    productPV: "250",
  },
  [SOP_PRODUCT_IMAGES[4]]: {
    productName: SOP_PRODUCT_NAMES[4],
    startDate: "202607",
    endDate: "202706",
    quantity: 1,
    frequency: 1,
    productPV: "320",
    promoMonths: ["202612"],
  },
  [SOP_PRODUCT_IMAGES[5]]: {
    productName: SOP_PRODUCT_NAMES[5],
    startDate: "202603",
    endDate: "202702",
    quantity: 2,
    frequency: 2,
    productPV: "180",
  },
  [SOP_PRODUCT_IMAGES[6]]: {
    productName: SOP_PRODUCT_NAMES[6],
    startDate: "202605",
    endDate: "202704",
    quantity: 1,
    frequency: 1,
    productPV: "150",
  },
};

const getPeriodMonthDifference = (startPeriod: string, endPeriod: string): number => {
  const startYear = Number(startPeriod.slice(0, 4));
  const startMonth = Number(startPeriod.slice(4, 6));
  const endYear = Number(endPeriod.slice(0, 4));
  const endMonth = Number(endPeriod.slice(4, 6));
  return (endYear - startYear) * 12 + endMonth - startMonth;
};

const isSubscriptionShippingInPeriod = (subscription: SopSubscription, period: string): boolean => {
  if (period < subscription.startDate || period > subscription.endDate) return false;
  return getPeriodMonthDifference(subscription.startDate, period) % subscription.frequency === 0;
};

const getNextPeriod = (period: string): string => {
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(4, 6));
  const nextDate = new Date(year, month, 1);
  return `${nextDate.getFullYear()}${String(nextDate.getMonth() + 1).padStart(2, "0")}`;
};

const getExpectedPVForPeriod = (subscriptions: SopSubscription[], period: string): string => {
  const expectedPV = subscriptions.reduce(
    (total, subscription) =>
      total +
      (isSubscriptionShippingInPeriod(subscription, period)
        ? subscription.quantity * Number(subscription.productPV.replaceAll(",", ""))
        : 0),
    0,
  );
  return new Intl.NumberFormat("en-US").format(expectedPV);
};

export const getSopStatusForAbo = (
  affAbo: string,
  currentPeriod: string = getCurrentPeriod(),
): { kpis: SopStatusKpis; subscriptions: SopSubscription[] } => {
  const reportRecord = Object.values(GROUP_SOP_MOCK_DATA)
    .flat()
    .find((record) => `${record.aff}-${record.abo}` === affAbo);
  if (!reportRecord) {
    return { kpis: SOP_STATUS_KPIS, subscriptions: SOP_STATUS_SUBSCRIPTIONS };
  }

  const subscriptions = reportRecord.productImages.map((imgUrl) => ({ ...SOP_PRODUCT_DETAILS[imgUrl], imgUrl }));
  const nextPeriod = getNextPeriod(currentPeriod);

  return {
    kpis: {
      expectedPVThisMonth: getExpectedPVForPeriod(subscriptions, currentPeriod),
      expectedPVNextMonth: getExpectedPVForPeriod(subscriptions, nextPeriod),
      productsOnSop: subscriptions.length,
      productsShippingThisMonth: subscriptions.filter((subscription) =>
        isSubscriptionShippingInPeriod(subscription, currentPeriod),
      ).length,
      ordersUpForRenewal: subscriptions.filter((subscription) => subscription.endDate === currentPeriod).length,
    },
    subscriptions,
  };
};

export type SopSubscriptionScheduleEntry = {
  month: number;
  monthName: string;
  qty: number | "" | "-";
  isFuture: boolean;
  isPromo: boolean;
  isExpiration: boolean;
};

export const getSopSubscriptionSchedule = (
  subscription: SopSubscription,
  locale: string,
): SopSubscriptionScheduleEntry[] =>
  getNextNPeriodNumbers(subscription.startDate, 12, true).map((period, index) => {
    const isFuture = period > Number(subscription.endDate);
    const qty = isFuture ? "-" : (index + 1) % subscription.frequency === 0 || index === 0 ? subscription.quantity : "";

    return {
      month: period,
      monthName: getShortMonth(period, locale),
      qty,
      isFuture,
      isPromo: subscription.promoMonths?.includes(period.toString()) ?? false,
      isExpiration: period === Number(subscription.endDate),
    };
  });

export const isSopSubscriptionUpForRenewal = (
  subscription: SopSubscription,
  currentPeriod: string = getCurrentPeriod(),
): boolean => subscription.endDate === currentPeriod;
